import React, { Fragment, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import _ from 'underscore';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Drawer, Divider, Paper, Avatar, Typography, Hidden } from '@mui/material';

import FolderIcon from '@mui/icons-material/FolderOutlined';

import useRouter from 'utils/useRouter';
import getInitials from 'utils/getInitials';
import { Navigation } from 'components';
import config from 'configuration/configuration.js';

import navigationConfig from './navigationConfig';

const useStyles = makeStyles((theme) => ({
	root: {
		height: '100%',
		overflowY: 'auto',
	},
	content: {
		padding: theme.spacing(2),
	},
	profile: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		minHeight: 'fit-content',
	},
	avatar: {
		width: 60,
		height: 60,
	},
	name: {
		marginTop: theme.spacing(1),
	},
	divider: {
		marginTop: theme.spacing(2),
	},
	navigation: {
		marginTop: theme.spacing(2),
	},
}));

const NavBar = (props) => {
	const { openMobile, onMobileClose, className, ...rest } = props;

	const classes = useStyles();
	const router = useRouter();
	const user = useSelector((state) => state.session.user);

	useEffect(() => {
		if (openMobile) {
			// eslint-disable-next-line no-unused-expressions
			onMobileClose && onMobileClose();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.location.pathname]);

	if (user.projects) {
		// replace hangfire token
		const management = navigationConfig.find(item => item.title === 'Management');
		const hangfire = management.pages.find(item => item.title === 'Hangfire');
		hangfire.href = hangfire.href.replace('{token}', localStorage.getItem(config.TOKEN_KEY));

		const hangfireindex = management.pages.findIndex(item => item.title === 'Hangfire');
		management.pages.splice(hangfireindex, 1, hangfire);

		const managementIndex = navigationConfig.findIndex(item => item.title === 'Management');
		navigationConfig.splice(managementIndex, 1, management);
	}

	const navbarContent = (
		<div className={classes.content}>
			<div className={classes.profile}>
				<Avatar
					alt="Person"
					className={classes.avatar}
					component={RouterLink}
					src={user.avatar}
					to="/home"
				>
					{getInitials(user.firstname)}
					{' '}
					{getInitials(user.lastname)}
				</Avatar>
				<Typography className={classes.name} variant="h4">
					{user.firstname}
					{' '}
					{user.lastname}
				</Typography>
				<Typography variant="body2">{user.operator}</Typography>
			</div>
			<Divider className={classes.divider} />
			<nav className={classes.navigation}>
				{navigationConfig.map((list) => (
					(!list.roles || list.roles.includes(user.role)) && (!list.projects || _.intersection(list.projects, user.projects).length !== 0)
						? (
							<Navigation
								component="div"
								key={list.title}
								pages={list.pages}
								title={list.title}
								userRole={user.role}
								userProjects={user.projects}
							/>
						) : null
				)
				)}
			</nav>
		</div>
	);

	return (
		<>
			<Hidden lgUp>
				<Drawer
					anchor="left"
					onClose={onMobileClose}
					open={openMobile}
					variant="temporary"
				>
					<div {...rest} className={clsx(classes.root, className)}>
						{navbarContent}
					</div>
				</Drawer>
			</Hidden>
			<Hidden lgDown>
				<Paper
					{...rest}
					className={clsx(classes.root, className)}
					elevation={1}
					square
				>
					{navbarContent}
				</Paper>
			</Hidden>
		</>
	);
};

NavBar.propTypes = {
	className: PropTypes.string,
	onMobileClose: PropTypes.func,
	openMobile: PropTypes.bool,
};

export default NavBar;
